import React from "react";
import { Box, Typography } from "@mui/material";
import { useScreenshotTranslation } from "../../../lib/screenshotI18n";
import { getImageUrl, getStoryTitle } from "../lib/api";
import {
    galleryHeaderAmbientSx,
    galleryHeadlineDarkSx,
    promoHeadlineSubtitleSx,
    vaultPackCardSx,
    vaultShellSx,
} from "../styles/screenshotStyles";

const JM = "mobileScreenshot.jigsaw";

const PACK_DEFS = [
    {
        key: "vault-pack-dragons",
        titleFallback: "Epic Dragons",
        solved: 8,
        total: 12,
    },
    {
        key: "vault-pack-art",
        titleFallback: "Impressionist Art",
        solved: 5,
        total: 10,
    },
    {
        key: "vault-pack-ocean",
        titleFallback: "Deep Ocean",
        solved: 3,
        total: 8,
    },
    {
        key: "vault-pack-nature",
        titleFallback: "Nature",
        solved: 7,
        total: 10,
    },
    {
        key: "vault-pack-cities",
        titleFallback: "Cities",
        solved: 4,
        total: 9,
    },
    {
        key: "vault-pack-seasons",
        titleFallback: "Seasons",
        solved: 6,
        total: 8,
    },
];

function PackCard({ title, imageUrl, solved, total, progressLabel, offset, fill = false }) {
    const pct = total > 0 ? Math.round((solved / total) * 100) : 0;
    return (
        <Box
            sx={{
                ...vaultPackCardSx,
                flex: fill ? undefined : "1 1 0",
                height: fill ? "100%" : undefined,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                transform: fill ? "none" : `translateY(${offset}px)`,
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    flex: "1 1 auto",
                    minHeight: fill ? 0 : { xs: 168, sm: 200 },
                    overflow: "hidden",
                }}
            >
                {imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt=""
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                ) : (
                    <Box sx={{ width: "100%", height: "100%", bgcolor: "#cbd5e1" }} />
                )}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(180deg, transparent 40%, rgba(15,23,42,0.55) 100%)",
                    }}
                />
                <Typography
                    sx={{
                        position: "absolute",
                        left: 16,
                        bottom: 14,
                        m: 0,
                        fontWeight: 800,
                        fontSize: { xs: "1.2rem", sm: "1.35rem" },
                        color: "#fff",
                        textShadow: "0 2px 10px rgba(0,0,0,0.45)",
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Box sx={{ px: 1.75, py: 1.5, flexShrink: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.85,
                    }}
                >
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#475569" }}>
                        {progressLabel}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#94a3b8" }}>
                        {pct}%
                    </Typography>
                </Box>
                <Box
                    sx={{
                        height: 10,
                        borderRadius: 999,
                        bgcolor: "#e2e8f0",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            width: `${pct}%`,
                            height: "100%",
                            borderRadius: 999,
                            background: "linear-gradient(90deg, #3BA666, #22c55e)",
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

/**
 * Shot 07 — immersive vault menu with layered pack-folder cards.
 */
export default function VaultPackMenu({ headline, subtitle, items = [], compact = false, landscape = false }) {
    const { t } = useScreenshotTranslation();
    const packCount = landscape ? 6 : 3;
    const packs = PACK_DEFS.slice(0, packCount);
    const covers = items.slice(0, packCount).map((item) => ({
        imageUrl: getImageUrl(item?.group?.story),
        fallbackTitle: getStoryTitle(item?.group?.story),
    }));

    return (
        <Box
            sx={{
                ...vaultShellSx,
                ...(compact
                    ? {
                          pt: "max(env(safe-area-inset-top, 0px), 8px)",
                          pb: "max(env(safe-area-inset-bottom, 0px), 8px)",
                      }
                    : {}),
            }}
        >
            <Box
                sx={{
                    ...galleryHeaderAmbientSx,
                    ...(compact ? { pt: 0.5, pb: 0.75 } : {}),
                }}
            >
                <Typography component="h1" sx={{ ...galleryHeadlineDarkSx, fontSize: { xs: "1.7rem", sm: "1.9rem" } }}>
                    {headline}
                </Typography>
                {subtitle ? (
                    <Typography component="p" sx={{ ...promoHeadlineSubtitleSx, mt: 0.75 }}>
                        {subtitle}
                    </Typography>
                ) : null}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    px: 2,
                    pb: 2,
                    display: landscape ? "grid" : "flex",
                    flexDirection: landscape ? undefined : "column",
                    gridTemplateColumns: landscape ? "repeat(3, minmax(0, 1fr))" : undefined,
                    gridTemplateRows: landscape ? "repeat(2, minmax(0, 1fr))" : undefined,
                    gap: compact ? 1.25 : 1.75,
                    justifyContent: "stretch",
                }}
            >
                {packs.map((pack, i) => {
                    const cover = covers[i] || {};
                    const progressKey = `${JM}.vault-pack-progress`;
                    const progressRaw = t(progressKey, {
                        solved: pack.solved,
                        total: pack.total,
                    });
                    const progressLabel =
                        progressRaw === progressKey
                            ? `${pack.solved}/${pack.total} Solved`
                            : progressRaw;
                    return (
                        <PackCard
                            key={pack.key}
                            title={cover.fallbackTitle || t(`${JM}.${pack.key}`, pack.titleFallback)}
                            imageUrl={cover.imageUrl}
                            solved={pack.solved}
                            total={pack.total}
                            progressLabel={progressLabel}
                            offset={landscape ? 0 : i === 1 ? 4 : i === 2 ? -2 : 0}
                            fill={landscape}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}
